/**
 * Favourites (spec M1-B1 §4.4, §5.6, §7.2).
 *
 * A favourite is **per user** — the star in the sidebar row, on a workspace-home row and in
 * the board-info popover are the same row of the same table, and a second user never sees it.
 * monday exposes no favourites field, so this is tRPC-only (§8 "Not exposed on /v2").
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from './hooks';

export type FavoriteEntity = 'board' | 'doc' | 'dashboard' | 'workspace' | 'folder';

export function useFavorites() {
  const trpc = useTRPC();
  return useQuery({ ...trpc.favorites.list.queryOptions(), staleTime: 30_000 });
}

export function useFavorite(entity: FavoriteEntity, entityId: string) {
  const client = useTRPCClient();
  const favorites = useFavorites();
  const invalidate = useBoardInvalidation();

  const isFavorite = (favorites.data ?? []).some(
    (row) => row.entityType === entity && String(row.entityId) === String(entityId),
  );

  const mutation = useMutation({
    mutationFn: async (next: boolean) =>
      next
        ? client.favorites.add.mutate({ entityType: entity, entityId })
        : client.favorites.remove.mutate({ entityType: entity, entityId }),
    onSuccess: async () => {
      await favorites.refetch();
      await invalidate();
    },
  });

  return { isFavorite, toggle: () => mutation.mutate(!isFavorite), pending: mutation.isPending };
}
